import type { ClientHealth } from "@app/portal-sdk";
import { Box, Stack, Typography, bkdp } from "@app/ui-kit";

/**
 * SHL-4: a client's engagement health as a stoplight: three lamps in a vertical housing, the active one lit and the
 * other two dimmed to the surface, never hidden (a stoplight with one lamp is a dot). It never conveys state by colour
 * alone: the active lamp's word sits beside the housing and the control's accessible name reads the same. The colours
 * are the portal's validated three, the same the channel charts use; no new hues.
 *
 * Which lamp is which is the platform list client_health's three codes, in its order, which is why the list is
 * label_only: a fourth code would be a lamp nothing renders.
 */
export const HEALTH_COLOURS: Record<string, string> = { green: bkdp.teal, amber: bkdp.amber, red: bkdp.crimson };
const LAMPS = ["red", "amber", "green"] as const;

export const Stoplight = ({ health, onClick }: { health: ClientHealth; onClick?: () => void }) => {
  const name = `Engagement health: ${health.label}`;
  const body = (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, p: 0.75, borderRadius: 2, backgroundColor: bkdp.deepNavy }} aria-hidden="true">
        {LAMPS.map((code) => {
          const lit = code === health.code;
          return <Box key={code} data-testid={`lamp-${code}`} data-lit={lit ? "true" : "false"} sx={{ width: 14, height: 14, borderRadius: "50%", backgroundColor: lit ? HEALTH_COLOURS[code] : "rgba(255,255,255,0.14)", boxShadow: lit ? `0 0 6px ${HEALTH_COLOURS[code]}` : "none" }} />;
        })}
      </Box>
      <Stack spacing={0}>
        <Typography variant="overline" sx={{ lineHeight: 1.2, color: "text.secondary" }}>Where you stand</Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: HEALTH_COLOURS[health.code] ?? "text.primary", lineHeight: 1.2 }}>{health.label}</Typography>
      </Stack>
    </Stack>
  );
  return onClick ? (
    <Box component="button" type="button" onClick={onClick} aria-label={`${name}. Go to the engagement status.`} data-testid="stoplight"
      sx={{ border: 0, background: "none", p: 0.5, cursor: "pointer", font: "inherit", textAlign: "left", borderRadius: 2, "&:focus-visible": { outline: `2px solid ${bkdp.signalBlue}` } }}>
      {body}
    </Box>
  ) : (
    <Box role="img" aria-label={name} data-testid="stoplight" sx={{ p: 0.5 }}>{body}</Box>
  );
};

/** SHL-3: the colour with its word, for a list column. A dot and a word, never the dot alone. */
export const HealthWord = ({ health }: { health: ClientHealth }) => (
  <Stack direction="row" spacing={0.75} alignItems="center" component="span" aria-label={`Engagement health: ${health.label}`} data-testid="health-word">
    <Box component="span" aria-hidden="true" sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: HEALTH_COLOURS[health.code] ?? bkdp.slate, display: "inline-block", flexShrink: 0 }} />
    <Typography component="span" variant="body2">{health.label}</Typography>
  </Stack>
);
